import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Input, List, Avatar, Badge, message, Typography } from 'antd';
import { MessageOutlined, SendOutlined, CloseOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import api from '../api';
import Settings from '../../settings';

const { Text } = Typography;

const ChatWidget = () => {
    const [visible, setVisible] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [userInfo, setUserInfo] = useState({ name: 'Անանուն', age: 0 });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isAdminOnline, setIsAdminOnline] = useState(false);
    const [isActualAdminOnline, setIsActualAdminOnline] = useState(false);
    const messagesEndRef = useRef(null);

    const { quizes, questions, settings } = useSelector((state) => state);

    useEffect(() => {
        // Global version check to force update
        if (settings && settings.app_version && settings.app_version !== Settings.app_version) {
            console.log('Version mismatch detected, forcing update...');
            
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                    for (let registration of registrations) registration.unregister();
                });
            }
            if ('caches' in window) {
                caches.keys().then(names => {
                    for (let name of names) caches.delete(name);
                });
            }

            // Update version in redux so it doesn't loop
            // But we reload anyway
            setTimeout(() => {
               // window.location.reload(true);
            }, 1000);
        }
    }, [settings?.app_version]);

    const updateUserInfo = () => {
        const storedName = localStorage.getItem('chat_user_name');
        const storedAge = localStorage.getItem('chat_user_age');
        if (storedName) {
            setUserInfo({ name: storedName, age: storedAge || 0 });
        }
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        let storedId = localStorage.getItem('chat_session_id');
        if (!storedId) {
            storedId = 'user_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('chat_session_id', storedId);
        }
        setSessionId(storedId);

        updateUserInfo();
        window.addEventListener('user_info_updated', updateUserInfo);

        const pollInterval = setInterval(() => {
            api.API.get(api.Chat + '/poll', {
                headers: { 'X-Chat-Session-Id': storedId }
            }).then(response => {
                if (response) {
                    const { messages, force_show, pinned, clear, admin_online, update_app } = response;
                    
                    if (clear) {
                        setMessages([]);
                        setUnreadCount(0);
                        setVisible(false);
                        return;
                    }

                    if (update_app) {
                        message.loading('Թարմացվում է...', 2);
                        
                        // 1. Unregister Service Workers
                        if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.getRegistrations().then(registrations => {
                                for (let registration of registrations) {
                                    registration.unregister();
                                }
                            });
                        }

                        // 2. Clear Caches
                        if ('caches' in window) {
                            caches.keys().then(names => {
                                for (let name of names) caches.delete(name);
                            });
                        }

                        // 3. Reload
                        setTimeout(() => {
                           // window.location.reload(true);
                        }, 1500);
                    }

                    // Visibility now depends on global force_show/pinned AND real admin online status
                    setIsActualAdminOnline(!!admin_online);
                    setIsAdminOnline((!!force_show || !!pinned) && !!admin_online);
                    
                    if (messages && messages.length > 0) {
                        setMessages(prev => {
                            const next = [...prev];
                            messages.forEach(msg => {
                                const exists = next.some(m => m.id === msg.id || (m.timestamp === msg.timestamp && m.text === msg.text));
                                if (!exists) next.push(msg);
                            });
                            return next;
                        });
                        const hasAdminMessage = messages.some(msg => msg.from === 'admin');
                        if (hasAdminMessage && !visible) setVisible(true);
                        if (!visible) setUnreadCount(prev => prev + messages.length);
                    }
                }
            });
        }, 5000);

        return () => {
            clearInterval(pollInterval);
            window.removeEventListener('user_info_updated', updateUserInfo);
            window.removeEventListener('resize', handleResize);
        };
    }, [visible, quizes, questions]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        if (!sessionId) return;

        // 10s if admin is online, 40s if offline to save resources
        const intervalDuration = isActualAdminOnline ? 10000 : 40000;
        
        const heartbeatInterval = setInterval(() => {
            const name = localStorage.getItem('chat_user_name') || 'Անանուն';
            const age = localStorage.getItem('chat_user_age') || 0;
            
            const passedCount = Object.values(quizes || {}).length;
            const wrongAnswers = (questions || []).length;
            const trueAnswers = (passedCount * 20) - wrongAnswers;

            api.API.post(api.Chat + '/heartbeat', { 
                name, 
                age,
                progress: {
                    passed: passedCount,
                    true: trueAnswers,
                    wrong: wrongAnswers
                },
                current_page: window.location.pathname,
                app_version: Settings.app_version,
                push_time: Settings.push_time
            }, {
                headers: { 'X-Chat-Session-Id': sessionId }
            });
        }, intervalDuration);

        return () => clearInterval(heartbeatInterval);
    }, [sessionId, isActualAdminOnline, quizes, questions]);

    const handleSend = () => {
        if (!inputValue.trim()) return;
        const text = inputValue.trim();
        setInputValue('');
        api.API.post(api.Chat + '/send', { to_session_id: 'admin', text: text }, {
            headers: { 'X-Chat-Session-Id': sessionId }
        }).then((res) => {
            const myMsg = { 
                id: res?.message?.id || Math.random().toString(36).substr(2, 9),
                from: sessionId, 
                text: text, 
                timestamp: Math.floor(Date.now() / 1000) 
            };
            setMessages(prev => {
                if (prev.some(m => m.id === myMsg.id)) return prev;
                return [...prev, myMsg];
            });
        });
    };

    const handleClear = () => {
        setMessages([]);
        setVisible(false);
        api.API.post(api.Chat + '/close', {}, { headers: { 'X-Chat-Session-Id': sessionId } });
    };

    return (
        <>
            {(unreadCount > 0 || isAdminOnline) && !visible && (
                <div style={{ position: 'fixed', bottom: isMobile ? 20 : 30, right: isMobile ? 20 : 30, zIndex: 1000 }}>
                    <Badge count={unreadCount}>
                        <Button 
                            type="primary" 
                            shape="circle" 
                            size="large" 
                            icon={<MessageOutlined style={{ fontSize: 24 }} />} 
                            onClick={() => {
                                setVisible(true);
                                setUnreadCount(0);
                            }}
                            style={{ width: 60, height: 60, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                        />
                    </Badge>
                </div>
            )}

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar style={{ backgroundColor: '#1890ff' }} icon={<CustomerServiceOutlined />} />
                        <div>
                            <Text strong>AvtoDas</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 10 }}>{userInfo.name}</Text>
                        </div>
                    </div>
                }
                open={visible}
                onCancel={() => setVisible(false)}
                footer={null}
                width={isMobile ? '90%' : 380}
                style={{ 
                    position: 'fixed', 
                    bottom: isMobile ? 10 : 10,
                    right: isMobile ? '5%' : 10,
                    margin: 0, 
                    paddingBottom: 0 
                }}
                mask={isMobile}
                closable={true}
                className="messenger-modal"
            >
                <div style={{ height: isMobile ? '60vh' : 420, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '8px 15px', background: '#fff', borderBottom: '1px solid #eee' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Բարև, {userInfo.name} ({userInfo.age} տ․)</Text>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '15px', background: '#f5f7f9' }}>
                        <List
                            dataSource={messages}
                            renderItem={item => {
                                const isMe = item.from === sessionId;
                                return (
                                    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                                        <div style={{ 
                                            maxWidth: '85%', 
                                            padding: '8px 14px', 
                                            borderRadius: 18, 
                                            borderTopRightRadius: isMe ? 2 : 18,
                                            borderTopLeftRadius: isMe ? 20 : 4,
                                            background: isMe ? '#1890ff' : '#fff',
                                            color: isMe ? '#fff' : '#333',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                                            fontSize: '14px'
                                        }}>
                                            {item.text}
                                            {item.is_broadcast && !isMe && (
                                                <div style={{ fontSize: '9px', marginTop: 4, opacity: 0.6 }}>Հայտարարություն</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <div ref={messagesEndRef} />
                    </div>
                    <div style={{ padding: '12px 15px', borderTop: '1px solid #eee', display: 'flex', gap: 10, background: '#fff' }}>
                        <Input 
                            placeholder="Հարցրեք..." 
                            value={inputValue} 
                            onChange={e => setInputValue(e.target.value)}
                            onPressEnter={handleSend}
                            style={{ borderRadius: 20 }}
                            suffix={<SendOutlined onClick={handleSend} style={{ cursor: 'pointer', color: '#1890ff' }} />}
                        />
                        <Button shape="circle" icon={<CloseOutlined />} onClick={handleClear} size="small" style={{ fontSize: 9, border: 'none', color: '#ccc' }} />
                    </div>
                </div>
            </Modal>
            <style dangerouslySetInnerHTML={{ __html: `
                .messenger-modal .ant-modal-content {
                    border-radius: 20px !important;
                    overflow: hidden;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.15) !important;
                }
                .messenger-modal .ant-modal-header { padding: 12px 20px !important; }
                .messenger-modal .ant-modal-body { padding: 0 !important; }
                @media (max-width: 767px) {
                    .messenger-modal { max-width: 90vw !important; }
                }
            `}} />
        </>
    );
};

export default ChatWidget;
